import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import API, { authFetch } from './api';
import NegotiationBriefPanel from './NegotiationBriefPanel';

/* ------------------------------------------------------------------ */
/* Shared helpers                                                      */
/* ------------------------------------------------------------------ */

const fitColor = (pct) => {
  if (pct >= 85) return { text: 'text-[#00E676]', bg: 'bg-[#00E676]', hex: '#00E676' };
  if (pct >= 70) return { text: 'text-[#00D4FF]', bg: 'bg-[#00D4FF]', hex: '#00D4FF' };
  if (pct >= 55) return { text: 'text-yellow-400', bg: 'bg-yellow-400', hex: '#FACC15' };
  if (pct >= 40) return { text: 'text-orange-400', bg: 'bg-orange-400', hex: '#FB923C' };
  return { text: 'text-red-400', bg: 'bg-red-400', hex: '#F87171' };
};

const tierBadgeClasses = (tier) =>
  ({
    'ELITE FIT': 'bg-emerald-900/40 text-emerald-300 border-emerald-500/30',
    'STRONG FIT': 'bg-green-900/40 text-green-300 border-green-500/30',
    'GOOD FIT': 'bg-yellow-900/40 text-yellow-300 border-yellow-500/30',
    'MARGINAL FIT': 'bg-orange-900/40 text-orange-300 border-orange-500/30',
    'POOR FIT': 'bg-red-900/40 text-red-300 border-red-500/30',
  }[tier] || 'bg-gray-800 text-gray-300 border-gray-600/30');

const tierLeftBorder = (tier) =>
  ({
    'ELITE FIT': 'border-l-4 border-l-[#00E676]',
    'STRONG FIT': 'border-l-4 border-l-[#00D4FF]',
    'GOOD FIT': 'border-l-4 border-l-yellow-400',
    'MARGINAL FIT': 'border-l-4 border-l-orange-400',
    'POOR FIT': 'border-l-4 border-l-red-400',
  }[tier] || 'border-l-4 border-l-gray-600');

const listingTypePill = (lt) => {
  if (lt === 'for_sale')
    return { label: 'FOR SALE', classes: 'bg-green-900/40 text-green-300 border-green-600/40' };
  if (lt === 'for_lease')
    return { label: 'FOR LEASE', classes: 'bg-blue-900/40 text-blue-300 border-blue-600/40' };
  if (lt === 'for_sale_and_lease')
    return { label: 'SALE & LEASE', classes: 'bg-orange-900/40 text-orange-300 border-orange-600/40' };
  return null;
};

const fitPct = (score9) => (score9 == null ? 0 : Math.round((score9 / 9) * 100));
const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString()}`);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
const pct = (v, digits = 1) => (v == null ? '—' : `${Number(v).toFixed(digits)}%`);
// price/sqft is not stored in the DB — derive it from price and total sqft when both exist.
const pricePerSqft = (p) => (p?.price != null && p?.sqft ? p.price / p.sqft : null);
const cap = (s) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_) {
    return iso;
  }
};

const addressLine = (p) => {
  if (!p) return '';
  const cityPart = [p.city, p.state].filter(Boolean).join(', ');
  const tail = [cityPart, p.zip].filter(Boolean).join(' ');
  return [p.address, tail].filter(Boolean).join(', ');
};

const propertyTypeChip = (p) => {
  if (!p?.property_type) return null;
  return (
    <span className="px-2 py-1 text-xs uppercase tracking-wider rounded border bg-cyan-900/40 text-cyan-300 border-cyan-600/40">
      {cap(p.property_type)}
    </span>
  );
};

const listingStatusPill = (status) => {
  if (!status) return null;
  const map = {
    active: 'bg-green-900/40 text-green-300 border-green-600/40',
    pending: 'bg-yellow-900/40 text-yellow-300 border-yellow-600/40',
    sold: 'bg-gray-800 text-gray-300 border-gray-600/40',
    leased: 'bg-gray-800 text-gray-300 border-gray-600/40',
    inactive: 'bg-red-900/40 text-red-300 border-red-600/40',
  };
  const cls = map[String(status).toLowerCase()] || 'bg-gray-800 text-gray-300 border-gray-600/40';
  return (
    <span className={`px-2 py-1 text-xs uppercase tracking-wider rounded border ${cls}`}>
      {cap(status)}
    </span>
  );
};

const ListingPill = ({ lt }) => {
  const p = listingTypePill(lt);
  if (!p) return null;
  return (
    <span className={`px-2 py-1 text-xs uppercase tracking-wider rounded border ${p.classes}`}>
      {p.label}
    </span>
  );
};

const TierPill = ({ tier }) => {
  if (!tier) return null;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${tierBadgeClasses(
        tier
      )}`}
    >
      {tier}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* PrintBar                                                            */
/* ------------------------------------------------------------------ */

const PrintBar = ({ title }) => (
  <div className="bg-[#1A2744] border-b border-[#546E7A]/30 print:hidden no-print">
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4">
      <Link
        to="/reports"
        className="flex items-center gap-2 text-gray-300 hover:text-[#00D4FF] text-sm font-medium transition-colors shrink-0"
      >
        ← Back
      </Link>
      {title && (
        <div className="hidden md:block flex-1 min-w-0 text-center text-white text-sm font-semibold truncate">
          {title}
        </div>
      )}
      <button
        onClick={() => window.print()}
        className="px-4 py-2 bg-[#FF6B35] text-white font-semibold rounded-lg hover:bg-[#E65A28] transition-colors text-sm whitespace-nowrap shrink-0"
      >
        🖨 <span className="hidden sm:inline">Print / Save as PDF</span><span className="sm:hidden">Print</span>
      </button>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Print CSS                                                           */
/* ------------------------------------------------------------------ */

const PRINT_CSS = `
@media print {
  @page { size: letter; margin: 0.75in; }
  html, body { background: white !important; color: black !important; }
  nav, .no-print { display: none !important; }
  [class*="bg-[#1A2744]"], [class*="bg-[#0A0E27]"], [class*="bg-[#1A1A2E]"] {
    background: white !important;
    color: #1A1A1A !important;
    border-color: #E5E7EB !important;
  }
  [class*="text-white"], [class*="text-gray-200"], [class*="text-gray-300"], [class*="text-gray-400"] {
    color: #1A1A1A !important;
  }
  [class*="text-gray-500"], [class*="text-gray-600"] {
    color: #6B7280 !important;
  }
  /* Strip link styling on paper — cyan underlined links look bad in print. */
  a, a:hover, a:visited { color: inherit !important; text-decoration: none !important; }
  .card { break-inside: avoid; }
  .page-break { break-before: page; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
`;

/* ------------------------------------------------------------------ */
/* Link helpers — used throughout the 5 report renderers.              */
/* They degrade to a plain fragment if the target id is missing, so    */
/* legacy data without ids still renders cleanly.                      */
/* ------------------------------------------------------------------ */
const PropertyLink = ({ property, className = '', children }) => {
  if (!property?.id) return <>{children}</>;
  return (
    <Link
      to={`/properties/${property.id}`}
      className={`text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors ${className}`}
    >
      {children}
    </Link>
  );
};

const ClientLink = ({ buyer, className = '', children }) => {
  if (!buyer?.id) return <>{children}</>;
  return (
    <Link
      to={`/match-board?buyer=${buyer.id}`}
      className={`text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors ${className}`}
    >
      {children}
    </Link>
  );
};

const EvalLink = ({ propertyId, buyerId, className = '', children, title }) => {
  if (!propertyId || !buyerId) return <>{children}</>;
  return (
    <Link
      to={`/properties/${propertyId}/buyer/${buyerId}`}
      className={`hover:opacity-90 transition-opacity inline-block ${className}`}
      title={title || 'Open property evaluation'}
    >
      {children}
    </Link>
  );
};

/* ------------------------------------------------------------------ */
/* Reusable atoms                                                      */
/* ------------------------------------------------------------------ */

const Card = ({ children, className = '', accent = '' }) => (
  <div
    className={`card bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 md:p-6 ${accent} ${className}`}
  >
    {children}
  </div>
);

const SectionHeader = ({ children, className = '' }) => (
  <h2 className={`text-white font-semibold text-lg ${className}`}>{children}</h2>
);

const Label = ({ children }) => (
  <div className="text-gray-500 text-xs uppercase tracking-wider">{children}</div>
);

const SpecValue = ({ children }) => (
  <div className="text-white font-bold text-lg">{children}</div>
);

const Spec = ({ label, value }) => (
  <div>
    <Label>{label}</Label>
    <SpecValue>{value ?? '—'}</SpecValue>
  </div>
);

const FitBar = ({ score9, color, showValue = true }) => {
  const p = fitPct(score9);
  const c = color || fitColor(p);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 bg-gray-800 rounded h-3 overflow-hidden print:bg-gray-200">
        <div className={`${c.bg} h-full rounded`} style={{ width: `${p}%` }} />
      </div>
      {showValue && (
        <div className={`${c.text} font-semibold text-sm w-16 text-right`}>
          {score9 == null ? '—' : Number(score9).toFixed(1)}/9
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Property Flyer                                                      */
/* ------------------------------------------------------------------ */

const PropertyFlyer = ({ data }) => {
  const p = data.property || {};
  // Listing broker contact lives on the property record itself (the external contact info).
  // data.listing_agent is the in-system AGENT that owns the listing — separate concept.
  const broker = {
    name: p.listing_broker_name || data.listing_agent?.name,
    company: p.listing_broker_company,
    phone: p.listing_broker_phone,
    email: p.listing_broker_email,
  };

  const financialRows = [
    { label: 'Cap Rate', value: p.cap_rate == null ? null : pct(p.cap_rate) },
    { label: 'NOI', value: p.noi == null ? null : money(p.noi) },
    { label: 'Occupancy', value: p.occupancy_rate == null ? null : pct(p.occupancy_rate) },
    { label: 'CAM Charges', value: p.cam_charges == null ? null : money(p.cam_charges) },
    { label: 'TI Allowance', value: p.ti_allowance == null ? null : money(p.ti_allowance) },
    { label: 'Annual Taxes', value: p.annual_taxes == null ? null : money(p.annual_taxes) },
  ].filter((r) => r.value != null);

  const hasIndustrial = [
    p.drive_in_doors,
    p.rail_access,
    p.column_spacing,
    p.floor_load_capacity,
    p.power_type,
    p.construction_type,
    p.roof_type,
  ].some((v) => v != null && v !== '');

  const highlights = Array.isArray(p.investment_highlights)
    ? p.investment_highlights
    : typeof p.investment_highlights === 'string' && p.investment_highlights
    ? p.investment_highlights.split(/\n|•|;|\|/).map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <Card accent="border-l-4 border-l-[#FF6B35]">
        <h1 className="text-white text-3xl md:text-4xl font-bold leading-tight">
          <PropertyLink property={p} className="hover:underline">
            {p.property_name || p.address || 'Property'}
          </PropertyLink>
        </h1>
        <div className="text-gray-400 mt-2 text-base">{addressLine(p)}</div>
        <div className="flex flex-wrap gap-2 mt-4">
          {propertyTypeChip(p)}
          <ListingPill lt={p.listing_type} />
          {listingStatusPill(p.listing_status)}
        </div>
      </Card>

      {/* Main 2-col layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT col */}
        <div className="lg:col-span-2 space-y-5">
          <Card accent="border-l-4 border-l-[#FF6B35]">
            <SectionHeader className="mb-4">Key Specs</SectionHeader>
            {/* Headline price / lease rate — kept inside Key Specs so it always lands on page 1 of print. */}
            {(p.price != null || p.lease_rate_sqft != null) && (
              <div className="mb-5 pb-5 border-b border-gray-700 flex flex-wrap items-end gap-8">
                {p.price != null && (
                  <div>
                    <Label>Sale Price</Label>
                    <div className="text-[#00E676] font-bold text-3xl md:text-4xl leading-tight">{money(p.price)}</div>
                    {pricePerSqft(p) != null && (
                      <div className="text-gray-400 text-xs mt-0.5">{money(pricePerSqft(p))} / sqft</div>
                    )}
                  </div>
                )}
                {p.lease_rate_sqft != null && (
                  <div>
                    <Label>Lease Rate</Label>
                    <div className="text-[#00E676] font-bold text-2xl md:text-3xl leading-tight">
                      {money(p.lease_rate_sqft)}<span className="text-sm text-gray-400 font-normal"> /sqft</span>
                    </div>
                    {p.lease_type && (
                      <div className="text-gray-400 text-xs mt-0.5">{cap(p.lease_type)}</div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Spec label="Sqft" value={num(p.sqft)} />
              <Spec label="Class" value={p.building_class ? cap(p.building_class) : '—'} />
              <Spec label="Year" value={p.year_built || '—'} />
              <Spec label="Stories" value={p.stories || '—'} />
            </div>
          </Card>

          <Card>
            <SectionHeader className="mb-4">Additional Specs</SectionHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Spec
                label="Ceiling Height"
                value={p.ceiling_height_ft ? `${p.ceiling_height_ft} ft` : '—'}
              />
              <Spec label="Loading Docks" value={p.loading_docks ?? '—'} />
              <Spec
                label="Parking"
                value={
                  p.parking_spaces
                    ? `${num(p.parking_spaces)} spaces`
                    : p.parking_ratio
                    ? `${p.parking_ratio}/1000`
                    : '—'
                }
              />
              <Spec
                label="Lot Size"
                value={p.lot_size_acres ? `${p.lot_size_acres} ac` : p.lot_size ? num(p.lot_size) : '—'}
              />
            </div>
          </Card>

          {p.description && (
            <Card>
              <SectionHeader className="mb-3">Description</SectionHeader>
              <div className="text-gray-200 text-sm leading-relaxed whitespace-pre-line">
                {p.description}
              </div>
            </Card>
          )}
        </div>

        {/* RIGHT col — headline price moved into Key Specs to guarantee page-1 placement,
            so this card now just summarizes the deal economics. Hidden if there's nothing to show. */}
        {financialRows.length > 0 && (
          <div className="space-y-5">
            <Card>
              <SectionHeader className="mb-4">Financial Summary</SectionHeader>
              <div className="space-y-2">
                {financialRows.map((r) => (
                  <div key={r.label} className="flex justify-between text-sm">
                    <span className="text-gray-400">{r.label}</span>
                    <span className="text-white font-semibold">{r.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Industrial Features (full width, conditional) */}
      {hasIndustrial && (
        <Card>
          <SectionHeader className="mb-4">Industrial Features</SectionHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {p.drive_in_doors != null && <Spec label="Drive-In Doors" value={p.drive_in_doors} />}
            {p.rail_access != null && (
              <Spec label="Rail Access" value={p.rail_access ? 'Yes' : 'No'} />
            )}
            {p.column_spacing && <Spec label="Column Spacing" value={p.column_spacing} />}
            {p.floor_load_capacity && (
              <Spec label="Floor Load" value={p.floor_load_capacity} />
            )}
            {p.power_type && <Spec label="Power" value={p.power_type} />}
            {p.construction_type && <Spec label="Construction" value={cap(p.construction_type)} />}
            {p.roof_type && <Spec label="Roof" value={cap(p.roof_type)} />}
          </div>
        </Card>
      )}

      {/* Investment Highlights */}
      {highlights.length > 0 && (
        <Card>
          <SectionHeader className="mb-3">Investment Highlights</SectionHeader>
          <ul className="space-y-2">
            {highlights.map((h, i) => (
              <li key={i} className="text-gray-200 text-sm flex gap-2">
                <span className="text-[#FF6B35] font-bold">▸</span>
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Listing Broker */}
      {(broker?.name || broker?.email || broker?.phone) && (
        <Card className="max-w-md ml-auto">
          <Label>Listing Broker</Label>
          <div className="text-white font-bold text-lg mt-1">{broker.name || '—'}</div>
          {broker.company && <div className="text-gray-400 text-sm">{broker.company}</div>}
          <div className="mt-2 text-sm space-y-0.5">
            {broker.phone && (
              <div>
                <a href={`tel:${broker.phone}`} className="text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors">
                  {broker.phone}
                </a>
              </div>
            )}
            {broker.email && (
              <div>
                <a href={`mailto:${broker.email}`} className="text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors break-all">
                  {broker.email}
                </a>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Tour Book                                                           */
/* ------------------------------------------------------------------ */

const TourBook = ({ data }) => {
  const properties = data.properties || [];
  const buyer = data.buyer || {};
  const agent = data.agent || {};

  return (
    <div className="space-y-6">
      {/* Cover */}
      <div className="card bg-[#1A1A2E] rounded-xl border border-[#546E7A]/30 p-8 md:p-12 text-center">
        <div className="text-gray-400 text-xs uppercase tracking-[0.3em] mb-3">
          Property Tour Book
        </div>
        <div
          className="mx-auto mb-6"
          style={{ borderTop: '3px solid #FF6B35', width: '80px' }}
        />
        <h1 className="text-white text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: 'Georgia, serif' }}>
          {buyer?.id ? (
            <Link
              to={`/match-board?buyer=${buyer.id}`}
              style={{ color: '#FFFFFF' }}
              className="hover:text-[#00E676] hover:underline transition-colors"
            >
              {buyer.name || 'Client'}
            </Link>
          ) : (buyer.name || 'Client')}
        </h1>
        {agent?.name && (
          <div className="text-gray-300 text-base mt-4">Prepared by {agent.name}</div>
        )}
        {data.date_iso && (
          <div className="text-gray-400 text-sm mt-1">{formatDate(data.date_iso)}</div>
        )}
      </div>

      {/* TOC */}
      <Card>
        <SectionHeader className="mb-4">Table of Contents</SectionHeader>
        <ol className="space-y-2">
          {properties.map((entry, i) => {
            const p = entry.property || {};
            const fp = fitPct(entry.fit_score);
            const c = fitColor(fp);
            return (
              <li key={p.id || i} className="flex items-center gap-3">
                <span className="text-gray-500 w-6 text-sm font-mono">{i + 1}.</span>
                <a
                  href={`#prop-${p.id}`}
                  className="flex-1 text-white hover:text-[#00D4FF] truncate"
                >
                  {p.property_name || p.address || 'Property'}
                  {p.address && p.property_name && (
                    <span className="text-gray-500 text-xs ml-2">{p.address}</span>
                  )}
                </a>
                <span className={`${c.text} font-semibold text-sm`}>{fp}%</span>
              </li>
            );
          })}
        </ol>
      </Card>

      {/* Per-property */}
      {properties.map((entry, i) => {
        const p = entry.property || {};
        const fp = fitPct(entry.fit_score);
        const c = fitColor(fp);
        const dims = entry.dimension_scores || {};

        return (
          <div key={p.id || i} id={`prop-${p.id}`} className="page-break space-y-5">
            <Card>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Stop #{i + 1}
                  </div>
                  <h2 className="text-white text-2xl md:text-3xl font-bold">
                    <PropertyLink property={p} className="hover:underline">
                      {p.property_name || p.address || 'Property'}
                    </PropertyLink>
                  </h2>
                  <div className="text-gray-400 mt-1">{addressLine(p)}</div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {propertyTypeChip(p)}
                    <ListingPill lt={p.listing_type} />
                  </div>
                </div>
                <EvalLink propertyId={p.id} buyerId={buyer?.id} className="text-right">
                  <div className={`${c.text} text-5xl font-bold leading-none`}>{fp}%</div>
                  <div className="mt-2">
                    <TierPill tier={entry.tier} />
                  </div>
                </EvalLink>
              </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <Card className="lg:col-span-2">
                <SectionHeader className="mb-4">Key Specs</SectionHeader>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Spec label="Sqft" value={num(p.sqft)} />
                  <Spec label="Class" value={p.building_class ? cap(p.building_class) : '—'} />
                  <Spec label="Year Built" value={p.year_built || '—'} />
                  <Spec
                    label="Ceiling Height"
                    value={p.ceiling_height_ft ? `${p.ceiling_height_ft} ft` : '—'}
                  />
                </div>
              </Card>

              <Card accent="border-l-4 border-l-[#FF6B35]">
                <Label>{p.price != null ? 'Sale Price' : 'Lease Rate'}</Label>
                <div className="text-[#00E676] font-bold text-2xl mt-1">
                  {p.price != null
                    ? money(p.price)
                    : p.lease_rate_sqft != null
                    ? `${money(p.lease_rate_sqft)}/sqft`
                    : '—'}
                </div>
                {p.price != null && pricePerSqft(p) != null && (
                  <div className="text-gray-400 text-xs mt-1">
                    {money(pricePerSqft(p))} / sqft
                  </div>
                )}
                {p.lease_type && (
                  <div className="text-gray-400 text-xs mt-1">{cap(p.lease_type)}</div>
                )}
              </Card>
            </div>

            {/* Category Scores */}
            {Object.keys(dims).length > 0 && (
              <Card>
                <SectionHeader className="mb-4">Category Scores</SectionHeader>
                <div className="space-y-3">
                  {Object.entries(dims).map(([name, score]) => {
                    const sp = fitPct(score);
                    const sc = fitColor(sp);
                    return (
                      <div key={name} className="grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-4 text-gray-300 text-sm">{name}</div>
                        <div className="col-span-8">
                          <FitBar score9={score} color={sc} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Strengths / Concerns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Card accent="border-l-4 border-l-[#00E676]">
                <SectionHeader className="mb-3 text-[#00E676]">Top Strengths</SectionHeader>
                {entry.top_strengths?.length ? (
                  <ul className="space-y-2">
                    {entry.top_strengths.map((s, idx) => (
                      <li key={idx} className="flex items-start justify-between gap-2 text-sm">
                        <div>
                          <div className="text-white">{s.name}</div>
                          {s.category && (
                            <div className="text-gray-500 text-xs">{s.category}</div>
                          )}
                        </div>
                        <span className="text-[#00E676] font-semibold whitespace-nowrap">
                          {Number(s.score).toFixed(1)}/9
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500 text-sm">No data</div>
                )}
              </Card>

              <Card accent="border-l-4 border-l-[#FF6B35]">
                <SectionHeader className="mb-3 text-[#FF6B35]">Top Concerns</SectionHeader>
                {entry.top_concerns?.length ? (
                  <ul className="space-y-2">
                    {entry.top_concerns.map((s, idx) => (
                      <li key={idx} className="flex items-start justify-between gap-2 text-sm">
                        <div>
                          <div className="text-white">{s.name}</div>
                          {s.category && (
                            <div className="text-gray-500 text-xs">{s.category}</div>
                          )}
                        </div>
                        <span className="text-orange-400 font-semibold whitespace-nowrap">
                          {Number(s.score).toFixed(1)}/9
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500 text-sm">No data</div>
                )}
              </Card>
            </div>

            {/* Tour Notes */}
            <Card>
              <SectionHeader className="mb-3">Tour Notes</SectionHeader>
              <div className="space-y-3">
                {Array.from({ length: 8 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="border-b border-gray-700 h-6 print:border-gray-400"
                  />
                ))}
              </div>
            </Card>
          </div>
        );
      })}

      {/* Final summary */}
      {properties.length > 0 && (
        <Card>
          <SectionHeader className="mb-4">Side-by-Side Summary</SectionHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-left border-b border-gray-700">
                  <th className="px-2 py-2 font-medium">#</th>
                  <th className="px-2 py-2 font-medium">Address</th>
                  <th className="px-2 py-2 font-medium">Fit</th>
                  <th className="px-2 py-2 font-medium">Price / Lease</th>
                  <th className="px-2 py-2 font-medium">Sqft</th>
                  <th className="px-2 py-2 font-medium">Year</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((entry, idx) => {
                  const p = entry.property || {};
                  const fp = fitPct(entry.fit_score);
                  const c = fitColor(fp);
                  const isBest = idx === 0;
                  return (
                    <tr
                      key={p.id || idx}
                      className={`${idx % 2 === 0 ? 'bg-[#0A0E27]/40' : ''} ${
                        isBest ? 'bg-[#00E676]/10' : ''
                      }`}
                    >
                      <td className="px-2 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-2 py-2 text-white">
                        <PropertyLink property={p} className="hover:underline">
                          {p.property_name || p.address || '—'}
                        </PropertyLink>
                      </td>
                      <td className={`px-2 py-2 font-semibold ${c.text}`}>
                        <EvalLink propertyId={p.id} buyerId={buyer?.id}>
                          {fp}%
                        </EvalLink>
                      </td>
                      <td className="px-2 py-2 text-gray-200">
                        {p.price != null
                          ? money(p.price)
                          : p.lease_rate_sqft != null
                          ? `${money(p.lease_rate_sqft)}/sqft`
                          : '—'}
                      </td>
                      <td className="px-2 py-2 text-gray-200">{num(p.sqft)}</td>
                      <td className="px-2 py-2 text-gray-200">{p.year_built || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Comparison                                                          */
/* ------------------------------------------------------------------ */

const COMPARISON_METRICS = [
  { key: 'sqft', label: 'Sqft', best: 'sqft', fmt: (p) => num(p.sqft) },
  { key: 'price', label: 'Sale Price', best: 'price', fmt: (p) => money(p.price) },
  {
    key: 'price_per_sqft',
    label: 'Price/Sqft',
    best: null,
    fmt: (p) => (pricePerSqft(p) == null ? '—' : money(pricePerSqft(p))),
  },
  {
    key: 'lease_rate_sqft',
    label: 'Lease Rate',
    best: 'lease_rate_sqft',
    fmt: (p) => (p.lease_rate_sqft == null ? '—' : `${money(p.lease_rate_sqft)}/sqft`),
  },
  { key: 'lease_type', label: 'Lease Type', best: null, fmt: (p) => (p.lease_type ? cap(p.lease_type) : '—') },
  { key: 'cap_rate', label: 'Cap Rate', best: 'cap_rate', fmt: (p) => (p.cap_rate == null ? '—' : pct(p.cap_rate)) },
  { key: 'noi', label: 'NOI', best: 'noi', fmt: (p) => money(p.noi) },
  { key: 'cam_charges', label: 'CAM', best: 'cam_charges', fmt: (p) => money(p.cam_charges) },
  { key: 'ti_allowance', label: 'TI Allowance', best: 'ti_allowance', fmt: (p) => money(p.ti_allowance) },
  {
    key: 'building_class',
    label: 'Building Class',
    best: null,
    fmt: (p) => (p.building_class ? cap(p.building_class) : '—'),
  },
  { key: 'year_built', label: 'Year Built', best: 'year_built', fmt: (p) => p.year_built || '—' },
  {
    key: 'parking_ratio',
    label: 'Parking Ratio',
    best: 'parking_ratio',
    fmt: (p) => (p.parking_ratio == null ? '—' : `${p.parking_ratio}/1000`),
  },
  {
    key: 'occupancy_rate',
    label: 'Occupancy',
    best: 'occupancy_rate',
    fmt: (p) => (p.occupancy_rate == null ? '—' : pct(p.occupancy_rate)),
  },
  { key: 'zoning', label: 'Zoning', best: null, fmt: (p) => p.zoning || '—' },
];

const COMPARISON_COLORS = ['#00D4FF', '#00E676', '#FF6B35', '#9C27B0', '#FFC107'];

const Comparison = ({ data }) => {
  const properties = data.properties || [];
  const bestByField = data.best_index_by_field || {};
  const buyer = data.buyer || {};

  // Collect category names from first property
  const categoryNames = properties[0]?.dimension_scores
    ? Object.keys(properties[0].dimension_scores)
    : [];

  return (
    <div className="space-y-5">
      <Card accent="border-l-4 border-l-[#00E676]">
        <h1 className="text-white text-3xl md:text-4xl font-bold">{data.title || 'Property Comparison'}</h1>
        <div className="text-gray-400 mt-2">
          For{' '}
          <span className="text-white font-semibold">
            <ClientLink buyer={buyer} className="!text-white">{buyer.name || '—'}</ClientLink>
          </span>
          {data.date_iso && <span className="text-gray-500 ml-2">· {formatDate(data.date_iso)}</span>}
        </div>
      </Card>

      {/* Top stat row */}
      <Card>
        <SectionHeader className="mb-4">Fit Score Overview</SectionHeader>
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: `repeat(${Math.min(properties.length, 5)}, minmax(0, 1fr))`,
          }}
        >
          {properties.map((entry, idx) => {
            const p = entry.property || {};
            const fp = fitPct(entry.fit_score);
            const c = fitColor(fp);
            const isBestFit = bestByField.fit_score === idx;
            return (
              <div key={p.id || idx} className="text-center">
                <div className="text-gray-400 text-xs truncate" title={p.property_name || p.address}>
                  <PropertyLink property={p} className="hover:underline">
                    {p.property_name || p.address || '—'}
                  </PropertyLink>
                </div>
                <EvalLink propertyId={p.id} buyerId={buyer?.id}>
                  <div className={`${c.text} text-4xl font-bold mt-1`}>{fp}%</div>
                  <div className="mt-1">
                    <TierPill tier={entry.tier} />
                  </div>
                </EvalLink>
                {isBestFit && (
                  <div className="text-[#00E676] text-xs font-semibold mt-1">★ BEST FIT</div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Property cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {properties.map((entry, idx) => {
          const p = entry.property || {};
          const fp = fitPct(entry.fit_score);
          const c = fitColor(fp);
          return (
            <Card key={p.id || idx}>
              <div className="mb-3">
                <h3 className="text-white font-bold text-base truncate">
                  <PropertyLink property={p} className="hover:underline">
                    {p.property_name || p.address || 'Property'}
                  </PropertyLink>
                </h3>
                <div className="text-gray-500 text-xs truncate">{addressLine(p)}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {propertyTypeChip(p)}
                  <ListingPill lt={p.listing_type} />
                </div>
              </div>

              <EvalLink propertyId={p.id} buyerId={buyer?.id} className="block">
                <div className="flex items-center justify-between border-y border-gray-700 py-3 mb-3">
                  <div>
                    <Label>Fit Score</Label>
                    <div className={`${c.text} text-3xl font-bold`}>{fp}%</div>
                  </div>
                  <TierPill tier={entry.tier} />
                </div>
              </EvalLink>

              <div className="space-y-1.5">
                {COMPARISON_METRICS.map((m) => {
                  const isBest = m.best && bestByField[m.best] === idx;
                  return (
                    <div key={m.key} className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">{m.label}</span>
                      <span
                        className={`font-semibold flex items-center gap-1 ${
                          isBest ? 'text-[#00E676]' : 'text-white'
                        }`}
                      >
                        {m.fmt(p)}
                        {isBest && <span className="text-[#00E676]">✓</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Category Score Comparison */}
      {categoryNames.length > 0 && (
        <Card>
          <SectionHeader className="mb-3">Category Score Comparison</SectionHeader>
          <div className="flex flex-wrap gap-3 mb-4 text-xs">
            {properties.map((entry, idx) => {
              const p = entry.property || {};
              const color = COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
              return (
                <div key={p.id || idx} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded"
                    style={{ background: color }}
                  />
                  <span className="text-gray-300 truncate max-w-[140px]">
                    <PropertyLink property={p} className="!text-gray-300 hover:!text-[#00D4FF]">
                      {p.property_name || p.address || '—'}
                    </PropertyLink>
                  </span>
                </div>
              );
            })}
          </div>

          <div className="space-y-5">
            {categoryNames.map((cat) => (
              <div key={cat}>
                <div className="text-white text-sm font-semibold mb-2">{cat}</div>
                <div className="space-y-1.5">
                  {properties.map((entry, idx) => {
                    const color = COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
                    const score = entry.dimension_scores?.[cat];
                    const sp = fitPct(score);
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded shrink-0" style={{ background: color }} />
                        <div className="flex-1 bg-gray-800 rounded h-3 overflow-hidden print:bg-gray-200">
                          <div
                            className="h-full rounded"
                            style={{ width: `${sp}%`, background: color }}
                          />
                        </div>
                        <div className="text-white text-sm font-semibold w-16 text-right">
                          {score == null ? '—' : Number(score).toFixed(1)}/9
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Match Summary                                                       */
/* ------------------------------------------------------------------ */

const CRITERIA_COLORS = ['#00D4FF', '#00E676', '#FF6B35', '#9C27B0', '#FFC107'];

const MatchSummary = ({ data }) => {
  const buyer = data.buyer || {};
  const criteria = data.criteria_weights || [];
  const ranked = data.ranked_properties || [];

  return (
    <div className="space-y-5">
      <Card accent="border-l-4 border-l-[#9C27B0]">
        <h1 className="text-white text-3xl md:text-4xl font-bold">{data.title || 'Match Summary'}</h1>
        <div className="text-gray-400 mt-2">
          Client:{' '}
          <span className="text-white font-semibold">
            <ClientLink buyer={buyer} className="!text-white">{buyer.name || '—'}</ClientLink>
          </span>
          {data.date_iso && (
            <span className="text-gray-500 ml-2">· {formatDate(data.date_iso)}</span>
          )}
        </div>
      </Card>

      {criteria.length > 0 && (
        <Card>
          <SectionHeader className="mb-4">Client Criteria Weights</SectionHeader>
          <div className="space-y-3">
            {criteria.map((c, i) => {
              const weightPct = Math.round((c.weight || 0) * 100);
              const color = CRITERIA_COLORS[i % CRITERIA_COLORS.length];
              return (
                <div key={c.name} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-4 text-gray-300 text-sm">{c.name}</div>
                  <div className="col-span-7">
                    <div className="bg-gray-800 rounded h-3 overflow-hidden print:bg-gray-200">
                      <div
                        className="h-full rounded"
                        style={{ width: `${weightPct}%`, background: color }}
                      />
                    </div>
                  </div>
                  <div className="col-span-1 text-white text-sm font-semibold text-right">
                    {weightPct}%
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div>
        <SectionHeader className="mb-3">Ranked Properties</SectionHeader>
        <div className="space-y-3">
          {ranked.map((entry, idx) => {
            const p = entry.property || {};
            const fp = fitPct(entry.fit_score);
            const c = fitColor(fp);
            const isTop = entry.rank === 1 || idx === 0;
            const accent = isTop ? 'border-l-4 border-l-[#FF6B35]' : tierLeftBorder(entry.tier);
            return (
              <Card key={p.id || idx} accent={accent}>
                <div className="flex items-center gap-4">
                  <div className="text-gray-500 text-2xl font-bold w-12 text-center shrink-0">
                    #{entry.rank || idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-white font-bold text-base truncate">
                        <PropertyLink property={p} className="hover:underline">
                          {p.property_name || p.address || 'Property'}
                        </PropertyLink>
                      </h3>
                      <ListingPill lt={p.listing_type} />
                      {isTop && (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold border bg-orange-900/40 text-orange-300 border-orange-500/30">
                          TOP MATCH
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-xs truncate mt-0.5">
                      {addressLine(p)}
                    </div>
                    <div className="text-gray-400 text-xs mt-1">
                      {num(p.sqft)} sqft
                      {p.year_built ? ` · Built ${p.year_built}` : ''}
                      {p.property_type ? ` · ${cap(p.property_type)}` : ''}
                    </div>
                  </div>
                  <EvalLink propertyId={p.id} buyerId={buyer?.id} className="text-right shrink-0">
                    <div className={`${c.text} text-3xl font-bold leading-none`}>{fp}%</div>
                    <div className="mt-2">
                      <TierPill tier={entry.tier} />
                    </div>
                    <div className="text-gray-300 text-xs mt-2">
                      {p.price != null
                        ? money(p.price)
                        : p.lease_rate_sqft != null
                        ? `${money(p.lease_rate_sqft)}/sqft`
                        : ''}
                    </div>
                  </EvalLink>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Listing Interest                                                    */
/* ------------------------------------------------------------------ */

const ListingInterest = ({ data }) => {
  const p = data.property || {};
  // External listing-broker contact info lives on the property row itself.
  // Fall back to the in-system listing_agent's name if the broker name is absent.
  const broker = {
    name: p.listing_broker_name || data.listing_agent?.name,
    company: p.listing_broker_company,
    phone: p.listing_broker_phone,
    email: p.listing_broker_email,
  };
  const clients = data.matching_clients || [];

  return (
    <div className="space-y-5">
      <Card accent="border-l-4 border-l-yellow-400">
        <h1 className="text-white text-3xl md:text-4xl font-bold">
          <PropertyLink property={p} className="hover:underline">
            {p.property_name || p.address || 'Property'}
          </PropertyLink>
        </h1>
        <div className="text-gray-400 mt-2">{addressLine(p)}</div>
        <div className="flex flex-wrap gap-2 mt-3">
          {propertyTypeChip(p)}
          <ListingPill lt={p.listing_type} />
          {listingStatusPill(p.listing_status)}
        </div>
        {data.date_iso && (
          <div className="text-gray-500 text-xs mt-3">{formatDate(data.date_iso)}</div>
        )}
      </Card>

      {/* Property strip */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Spec label="Sqft" value={num(p.sqft)} />
          <Spec label="Class" value={p.building_class ? cap(p.building_class) : '—'} />
          <Spec label="Built" value={p.year_built || '—'} />
          <Spec
            label="Listing Type"
            value={p.listing_type ? cap(p.listing_type) : '—'}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-700 mt-4 pt-4">
          <div>
            <Label>{p.price != null ? 'Sale Price' : 'Lease Rate'}</Label>
            <div className="text-[#00E676] font-bold text-2xl mt-1">
              {p.price != null
                ? money(p.price)
                : p.lease_rate_sqft != null
                ? `${money(p.lease_rate_sqft)}/sqft`
                : '—'}
            </div>
          </div>
          {(broker?.name || broker?.email || broker?.phone) && (
            <div className="md:text-right">
              <Label>Listing Broker</Label>
              <div className="text-white font-semibold mt-1">{broker.name || '—'}</div>
              {broker.phone && (
                <div className="text-sm">
                  <a href={`tel:${broker.phone}`} className="text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors">
                    {broker.phone}
                  </a>
                </div>
              )}
              {broker.email && (
                <div className="text-sm">
                  <a href={`mailto:${broker.email}`} className="text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors break-all">
                    {broker.email}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Matching clients header */}
      <div className="flex items-center justify-between">
        <SectionHeader>Matching Clients</SectionHeader>
        <span className="px-3 py-1 rounded-full bg-[#1A2744] border border-[#00D4FF]/30 text-[#00D4FF] text-sm font-semibold">
          {clients.length} client{clients.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="space-y-3">
        {clients.map((entry, idx) => {
          const buyer = entry.buyer || {};
          const fp = fitPct(entry.fit_score);
          const c = fitColor(fp);
          return (
            <Card key={buyer.id || idx} accent={tierLeftBorder(entry.tier)}>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-lg">
                    <ClientLink buyer={buyer} className="!text-white hover:!text-[#00E676]">
                      {buyer.name || '—'}
                    </ClientLink>
                  </div>
                  <div className="mt-2">
                    <FitBar score9={entry.fit_score} color={c} showValue={false} />
                  </div>
                </div>
                <EvalLink propertyId={p.id} buyerId={buyer?.id} className="md:text-right md:w-32">
                  <div className={`${c.text} text-3xl font-bold leading-none`}>{fp}%</div>
                  <div className="mt-2">
                    <TierPill tier={entry.tier} />
                  </div>
                </EvalLink>
              </div>

              {entry.top_criteria?.length > 0 && (
                <div className="mt-4 border-t border-gray-700 pt-3">
                  <Label>Top Matching Criteria</Label>
                  <div className="mt-2 space-y-1.5">
                    {entry.top_criteria.map((crit, i) => {
                      const cp = fitPct(crit.score);
                      const cc = fitColor(cp);
                      return (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div className="min-w-0 flex-1">
                            <span className="text-white">{crit.name}</span>
                            {crit.category && (
                              <span className="text-gray-500 text-xs ml-2">{crit.category}</span>
                            )}
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-semibold ${cc.text} bg-gray-800/60 print:bg-gray-100`}
                          >
                            {Number(crit.score).toFixed(1)}/9
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
        {clients.length === 0 && (
          <Card>
            <div className="text-gray-400 text-sm text-center py-4">
              No clients have scored this property yet.
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Loss Analysis                                                       */
/* ------------------------------------------------------------------ */

// Lightweight markdown renderer tailored to the loss analysis output.
// Recognizes ## headings, numbered + bullet lists, **bold**, and paragraphs.
// Splits the report by `## XYZ` so each section can be rendered as its own card.
function splitReportSections(md) {
  if (!md) return [];
  const lines = md.split('\n');
  const sections = [];
  let current = null;
  for (const line of lines) {
    const m = line.match(/^##\s+(.+)$/);
    if (m) {
      if (current) sections.push(current);
      current = { heading: m[1].trim(), lines: [] };
    } else {
      if (!current) current = { heading: null, lines: [] };
      current.lines.push(line);
    }
  }
  if (current) sections.push(current);
  return sections;
}

function renderInlineBold(s, keyPrefix) {
  const parts = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push(<span key={`${keyPrefix}-t${i++}`}>{s.slice(last, m.index)}</span>);
    parts.push(<strong key={`${keyPrefix}-b${i++}`} className="text-white font-semibold">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push(<span key={`${keyPrefix}-t${i++}`}>{s.slice(last)}</span>);
  return parts;
}

function renderMarkdownBlock(lines, opts = {}) {
  const blocks = [];
  let cur = null;
  for (const raw of lines) {
    const line = raw.trimEnd();
    const numbered = line.match(/^\s*(\d+)\.\s+(.+)$/);
    const bullet = line.match(/^\s*[-*]\s+(.+)$/);
    if (numbered) {
      if (cur?.type !== 'ol') {
        if (cur) blocks.push(cur);
        cur = { type: 'ol', items: [] };
      }
      cur.items.push({ num: numbered[1], text: numbered[2] });
    } else if (bullet) {
      if (cur?.type !== 'ul') {
        if (cur) blocks.push(cur);
        cur = { type: 'ul', items: [] };
      }
      cur.items.push(bullet[1]);
    } else if (line.trim() === '') {
      if (cur) { blocks.push(cur); cur = null; }
    } else {
      if (cur?.type !== 'p') {
        if (cur) blocks.push(cur);
        cur = { type: 'p', lines: [] };
      }
      cur.lines.push(line);
    }
  }
  if (cur) blocks.push(cur);

  return blocks.map((b, i) => {
    if (b.type === 'ol') {
      return (
        <ol key={i} className="space-y-2 my-2">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-3 items-start">
              <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${opts.numbered === 'orange' ? 'bg-[#FF6B35] text-white' : 'bg-[#243B55] text-[#00D4FF]'}`}>
                {it.num}
              </span>
              <span className="text-gray-200 text-sm leading-relaxed pt-0.5">{renderInlineBold(it.text, `ol-${i}-${j}`)}</span>
            </li>
          ))}
        </ol>
      );
    }
    if (b.type === 'ul') {
      return (
        <ul key={i} className="list-disc ml-5 space-y-1 my-2 text-gray-200 text-sm">
          {b.items.map((it, j) => (
            <li key={j}>{renderInlineBold(it, `ul-${i}-${j}`)}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="text-gray-200 text-sm leading-relaxed my-2">
        {renderInlineBold(b.lines.join(' '), `p-${i}`)}
      </p>
    );
  });
}

// Per-section style — orange-accent for pattern / risk / recs.
const LOSS_SECTION_STYLE = {
  'EXECUTIVE SUMMARY':           { accent: 'border-l-4 border-l-[#00D4FF]', highlight: true },
  'LOSS BREAKDOWN':              { accent: 'border-l-4 border-l-red-500',   expandable: true },
  'PATTERN ANALYSIS':            { accent: 'border-l-4 border-l-[#FF6B35]', orangeBorder: true },
  'RISK ASSESSMENT FOR ACTIVE DEALS': { accent: 'border-l-4 border-l-[#FF6B35]', orangeBorder: true },
  'RECOMMENDATIONS':             { accent: 'border-l-4 border-l-[#FF6B35]', orangeNumbers: true },
  'KEY METRICS':                 { accent: 'border-l-4 border-l-[#00E676]' },
};

function LossAnalysis({ data }) {
  const ai = data.ai || {};
  const reportText = ai.report || '';
  const stats = ai.stats || data.stats || {};
  const byReason = data.by_reason || [];
  const sections = splitReportSections(reportText);

  return (
    <div className="space-y-5 print:break-inside-avoid">
      <Card accent="border-l-4 border-l-red-500">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-white text-3xl md:text-4xl font-bold">📉 Loss Analysis</h1>
            <div className="text-gray-400 text-sm mt-1">{data.date_iso}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-1 rounded text-xs font-bold border bg-red-500/15 text-red-300 border-red-500/40">
              {stats.total_losses ?? 0} lost
            </span>
            <span className="px-2 py-1 rounded text-xs font-bold border bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40">
              {stats.closed_count ?? 0} closed
            </span>
            {stats.loss_rate_pct != null && (
              <span className="px-2 py-1 rounded text-xs font-bold border bg-[#FFC107]/15 text-[#FFC107] border-[#FFC107]/40">
                {stats.loss_rate_pct}% loss rate
              </span>
            )}
          </div>
        </div>
      </Card>

      {sections.map((s, idx) => {
        const key = (s.heading || '').toUpperCase();
        const style = LOSS_SECTION_STYLE[key] || { accent: 'border-l-4 border-l-[#546E7A]' };
        const isExpandable = !!style.expandable;
        const isRecs = !!style.orangeNumbers;
        const isMetrics = key === 'KEY METRICS';

        // Loss breakdown — render the AI narrative followed by a structured
        // accordion of the actual lost deals grouped by reason.
        if (isExpandable) {
          return (
            <Card key={idx} accent={style.accent}>
              <SectionHeader>{s.heading}</SectionHeader>
              {renderMarkdownBlock(s.lines)}
              {byReason.length > 0 && (
                <div className="mt-4 space-y-2">
                  {byReason.map((g) => (
                    <details key={g.key} className="bg-[#0A0E27]/60 rounded-lg border border-[#546E7A]/20 group">
                      <summary className="cursor-pointer px-3 py-2 flex items-center justify-between list-none">
                        <span className="flex items-center gap-2">
                          <span className="text-white text-sm font-semibold">{g.label}</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold border bg-red-500/15 text-red-300 border-red-500/40">
                            {g.count}
                          </span>
                        </span>
                        <span className="text-gray-500 text-xs group-open:rotate-90 transition-transform">▶</span>
                      </summary>
                      <div className="px-3 pb-3 pt-1 space-y-2 border-t border-[#546E7A]/20">
                        {g.deals.map((d) => (
                          <div key={`${d.property_id}-${d.buyer_id}`} className="text-xs">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <EvalLink propertyId={d.property_id} buyerId={d.buyer_id} className="text-white font-semibold hover:underline">
                                {d.property_address}
                              </EvalLink>
                              <span className="text-gray-500">·</span>
                              <span className="text-gray-300">{d.buyer_name}</span>
                              {d.fit_pct != null && <span className="text-gray-500">· fit {d.fit_pct}%</span>}
                              {d.days_active != null && <span className="text-gray-500">· {d.days_active}d active</span>}
                            </div>
                            {d.loss_notes && (
                              <div className="text-gray-400 italic mt-0.5">"{d.loss_notes}"</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </Card>
          );
        }

        if (isMetrics) {
          // Use the structured stats for a tight grid; the AI prose for this
          // section is informative but redundant — show both.
          return (
            <Card key={idx} accent={style.accent}>
              <SectionHeader>{s.heading}</SectionHeader>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 my-3">
                <MetricCell label="Total Lost" value={stats.total_losses ?? '—'} />
                <MetricCell label="Closed" value={stats.closed_count ?? '—'} />
                <MetricCell label="Loss Rate" value={stats.loss_rate_pct != null ? `${stats.loss_rate_pct}%` : '—'} />
                <MetricCell label="Avg Days Active" value={stats.avg_days_active ?? '—'} />
                <MetricCell label="Top Reason" value={stats.most_common_reason || '—'} />
                <MetricCell
                  label="Avg Fit (lost vs closed)"
                  value={`${stats.avg_lost_fit_pct ?? '—'}% / ${stats.avg_closed_fit_pct ?? '—'}%`}
                />
              </div>
              {renderMarkdownBlock(s.lines)}
            </Card>
          );
        }

        return (
          <Card key={idx} accent={style.accent}>
            {s.heading && <SectionHeader>{s.heading}</SectionHeader>}
            {renderMarkdownBlock(s.lines, isRecs ? { numbered: 'orange' } : {})}
          </Card>
        );
      })}

      {(!reportText || sections.length === 0) && (
        <Card>
          <div className="text-gray-400 text-sm text-center py-4">
            No loss data available yet. Once deals are tagged as lost, this report will populate.
          </div>
        </Card>
      )}
    </div>
  );
}

const MetricCell = ({ label, value }) => (
  <div className="bg-[#0A0E27]/60 rounded-lg p-3 border border-[#546E7A]/20">
    <div className="text-gray-500 text-[10px] uppercase tracking-wider mb-1">{label}</div>
    <div className="text-white text-lg font-bold tabular-nums">{value}</div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Main viewer                                                         */
/* ------------------------------------------------------------------ */

export default function ReportViewer() {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const propertyId = searchParams.get('property_id');
  const buyerId = searchParams.get('buyer_id');
  const propertyIds = searchParams.get('property_ids');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadReport = async () => {
      setLoading(true);
      setError(null);
      setData(null);

      let endpoint = null;
      if (type === 'flyer') {
        if (!propertyId) {
          setError('Missing property_id');
          setLoading(false);
          return;
        }
        endpoint = `${API}/api/reports/property-flyer/${propertyId}`;
      } else if (type === 'tour-book') {
        if (!buyerId) {
          setError('Missing buyer_id');
          setLoading(false);
          return;
        }
        endpoint = `${API}/api/reports/tour-book/${buyerId}`;
      } else if (type === 'comparison') {
        if (!buyerId || !propertyIds) {
          setError('Missing buyer_id or property_ids');
          setLoading(false);
          return;
        }
        endpoint = `${API}/api/reports/comparison/${buyerId}?property_ids=${propertyIds}`;
      } else if (type === 'match-summary') {
        if (!buyerId) {
          setError('Missing buyer_id');
          setLoading(false);
          return;
        }
        endpoint = `${API}/api/reports/match-summary/${buyerId}`;
      } else if (type === 'listing-interest') {
        if (!propertyId) {
          setError('Missing property_id');
          setLoading(false);
          return;
        }
        endpoint = `${API}/api/reports/listing-interest/${propertyId}`;
      } else if (type === 'loss-analysis') {
        // Two-call report: structured data + AI analysis in parallel.
        // Broker drill-down: pass-through agent_id if present in the URL.
        const agentIdParam = searchParams.get('agent_id');
        const qs = agentIdParam ? `?agent_id=${encodeURIComponent(agentIdParam)}` : '';
        try {
          const [dataRes, aiRes] = await Promise.all([
            authFetch(`${API}/api/reports/loss-analysis${qs}`),
            authFetch(`${API}/api/insights/loss-analysis${qs}`),
          ]);
          if (!dataRes.ok || !aiRes.ok) {
            const failingRes = dataRes.ok ? aiRes : dataRes;
            let msg = `Failed to load report (${failingRes.status})`;
            try {
              const errData = await failingRes.json();
              if (errData.detail) msg = typeof errData.detail === 'string' ? errData.detail : msg;
            } catch (_) {}
            if (!cancelled) {
              setError(msg);
              setLoading(false);
            }
            return;
          }
          const dataJson = await dataRes.json();
          const aiJson = await aiRes.json();
          if (!cancelled) {
            setData({ ...dataJson, ai: aiJson });
            setLoading(false);
          }
        } catch (err) {
          console.error('Loss analysis load error:', err);
          if (!cancelled) {
            setError('Network error. Please try again.');
            setLoading(false);
          }
        }
        return;
      } else if (type === 'negotiation-brief') {
        // Negotiation brief — the panel component owns its own fetch + state,
        // so the viewer just needs to know the IDs. Mark "loaded" with the
        // identifiers so the renderer can mount the panel.
        if (!propertyId || !buyerId) {
          setError('Missing buyer_id or property_id');
          setLoading(false);
          return;
        }
        if (!cancelled) {
          setData({
            title: 'Negotiation Brief',
            property_id: Number(propertyId),
            buyer_id: Number(buyerId),
          });
          setLoading(false);
        }
        return;
      } else {
        setError(`Unknown report type: ${type || '(none)'}`);
        setLoading(false);
        return;
      }

      try {
        const res = await authFetch(endpoint);
        if (!res.ok) {
          let msg = `Failed to load report (${res.status})`;
          try {
            const errData = await res.json();
            if (errData.detail) msg = typeof errData.detail === 'string' ? errData.detail : msg;
          } catch (_) {
            /* ignore */
          }
          if (!cancelled) {
            setError(msg);
            setLoading(false);
          }
          return;
        }
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      } catch (err) {
        console.error('Report load error:', err);
        if (!cancelled) {
          setError('Network error. Please try again.');
          setLoading(false);
        }
      }
    };
    loadReport();
    return () => {
      cancelled = true;
    };
  }, [type, propertyId, buyerId, propertyIds]);

  // Set document title for nicer print/save
  useEffect(() => {
    const prev = document.title;
    if (data?.title) document.title = data.title;
    return () => {
      document.title = prev;
    };
  }, [data?.title]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading report…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="bg-[#1A2744] rounded-xl border border-red-600/40 p-8 text-center max-w-md">
          <div className="text-red-400 text-lg font-semibold mb-2">Could Not Load Report</div>
          <div className="text-gray-300 text-sm mb-4">{error}</div>
          <Link
            to="/reports"
            className="inline-block px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] text-sm"
          >
            ← Back to Reports
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#0A0E27] text-white print:bg-white print:text-black">
      <style>{PRINT_CSS}</style>
      <PrintBar title={data.title} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 print:p-0 print:max-w-none">
        {type === 'flyer' && <PropertyFlyer data={data} />}
        {type === 'tour-book' && <TourBook data={data} />}
        {type === 'comparison' && <Comparison data={data} />}
        {type === 'match-summary' && <MatchSummary data={data} />}
        {type === 'listing-interest' && <ListingInterest data={data} />}
        {type === 'loss-analysis' && <LossAnalysis data={data} />}
        {type === 'negotiation-brief' && data && (
          <NegotiationBriefPanel
            propertyId={data.property_id}
            buyerId={data.buyer_id}
            autoGenerate
          />
        )}
      </div>
    </div>
  );
}
